import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import { RootState } from "../../app/store";
import { CLAIM_TYPES, LOADING_STATUS, ROLES } from "../../common/constants";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";

const initialState = {
  identityClaims: null,
  authClaims: null,
  authStatus: LOADING_STATUS.LOADING,
};

export const getUserClaimsAsync = createAsyncThunk(
  "auth/getUserClaims",
  async () => {
    const identityClaims = await apiService.get(
      API_ENDPOINTS.IDENTITY.GET_IDENTITY,
    );

    const authClaims = await apiService.get(
      API_ENDPOINTS.IDENTITY.GET_AUTHORIZATION_CLAIMS,
    );
    return { identityClaims, authClaims };
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserClaimsAsync.pending, (state) => {
        state.authStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getUserClaimsAsync.fulfilled, (state, action) => {
        state.identityClaims = action.payload.identityClaims;
        state.authClaims = action.payload.authClaims;
        state.authStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getUserClaimsAsync.rejected, (state) => {
        state.identityClaims = null;
        state.authClaims = null;
        state.authStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectIdentityClaims = (state: RootState) =>
  state.auth.identityClaims;
export const selectAuthClaims = (state: RootState) => state.auth.authClaims;

export const selectRoleClaims = createSelector(
  [selectAuthClaims],
  (authClaims) => {
    const roleClaims = authClaims.filter(
      (c: { type: CLAIM_TYPES }) => c.type === CLAIM_TYPES.RolesType,
    );
    return roleClaims.map((roleClaim: { value: string }) => roleClaim.value);
  },
);

export const selectAuthStatus = (state: RootState) => state.auth.authStatus;

export const checkRole = (validRoles?: Array<ROLES>) =>
  createSelector([selectAuthClaims], (authClaims) => {
    if (!validRoles || validRoles.length === 0) {
      return true;
    }

    if (!authClaims) {
      return false;
    }

    return authClaims.some(
      (item: { type: CLAIM_TYPES; value: ROLES }) =>
        item.type === CLAIM_TYPES.RolesType &&
        validRoles.includes(<ROLES>item.value),
    );
  });

export const selectLoginFirstName = createSelector(
  [selectAuthClaims],
  (authClaims) => {
    const firstNameDictClaim = authClaims?.find(
      (claim) => claim.type === "given_name",
    );
    const lastNameDictClaim = authClaims?.find(
      (claim) => claim.type === "family_name",
    );
    return {
      firstName: firstNameDictClaim?.value ?? "",
      lastName: lastNameDictClaim?.value ?? "",
    };
  },
);

export const selectOwnerAccountId = createSelector(
  [selectAuthClaims],
  (authClaims) => {
    const ownerAccountIdClaim = authClaims?.find(
      (claim) => claim.type === "id",
    );
    return ownerAccountIdClaim?.value ?? "";
  },
);

export const selectOwnerNumber = createSelector(
  [selectAuthClaims],
  (authClaims) => {
    const ownerNumberClaim = authClaims?.find(
      (claim) => claim.type === "owner_number",
    );
    return ownerNumberClaim?.value ?? "";
  },
);

export const selectLogoutUrl = createSelector(
  [selectIdentityClaims],
  (identityClaims) => {
    const logoutUrlDictClaim = identityClaims?.find(
      (claim) => claim.type === "bff:logout_url",
    );
    return logoutUrlDictClaim?.value ?? "";
  },
);

export default authSlice.reducer;
